/**
 * ErrorBoundary Component
 * AIDLC Administrative Backend
 *
 * Catches a render error below it and shows a recoverable message instead of
 * unmounting the whole app (a blank white page). Used twice:
 *   - around the routed page inside MainLayout, keyed on the path, so the
 *     sidebar and header survive and moving to another page clears the error;
 *   - around the app root, as the last line if the layout itself throws.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Where the boundary sits, used in the console log. */
  scope: 'app' | 'page';
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[ErrorBoundary:${this.props.scope}]`, error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" data-testid="error-boundary" className="max-w-xl mx-auto mt-12 bg-white border border-red-200 rounded-lg p-6 dark:bg-zinc-900">
        <h1 className="text-base font-semibold text-red-800 dark:text-red-300 mb-2">
          {this.props.scope === 'app' ? 'The portal hit an error' : 'Something went wrong on this page'}
        </h1>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-4">
          {this.props.scope === 'app'
            ? 'Reload to continue.'
            : 'The rest of the portal still works. Reload to try again, or pick another page.'}
        </p>
        <pre className="text-[11px] bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-200 rounded p-2 mb-4 whitespace-pre-wrap break-words">
          {error.message || String(error)}
        </pre>
        <button
          type="button"
          onClick={this.handleReload}
          className="px-3 py-1.5 text-xs font-medium text-white bg-ldaja-blue rounded hover:opacity-90"
        >
          Reload
        </button>
      </div>
    );
  }
}
